import React, { useState } from 'react';
import {
    Button,
    Divider,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    makeStyles,
    mergeClasses,
    shorthands,
    tokens,
    Tooltip,
} from '@fluentui/react-components';
import {
    ChevronDoubleLeftFilled,
    ChevronDoubleRightFilled,
    ChevronDown16Regular,
    MoreHorizontal20Regular,
    ChevronRight16Regular,
} from '@fluentui/react-icons';
import useChatList from '../hooks/useChatList';
import { ChatListItemVm } from '../../../domain/selectors/view-models/chat/selectChatListViewModel';
import useChatListLayout from '../hooks/useChatListLayout';
import ChatListHeader from './ChatListHeader';
import ChatListRecentlyUsed from './ChatListRecentlyUsed';
import ChatListItem from './ChatListItem';
import ManageChatsDialog from './dialogs/ManageChatsDialog';
import ChatNavigationBackground from '../../../assets/Chat-Navigation-Background.svg';
import { ScrollBarStyles, customColors } from '../../../styles';
import { buildAvatarSrc } from '../../utils/AvatarUtils';
import { useAppDispatch } from '../../../redux/hooks';
import { setIsCollapsed, setIsOverlay } from '../../../redux/features/layout/layoutSlice';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        backgroundImage: `url(${ChatNavigationBackground})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'bottom center',
        '@media (max-width: 1000px)': {
            minWidth: '10px !important',
            width: '64px',
        },
    },
    rootCollapsed: {
        minWidth: '10px !important',
        width: '64px',
        maxWidth: '64px',
        flexBasis: '64px',
        overflow: 'hidden',
        alignItems: 'center',
    },
    rootSmallScreen: {
        minWidth: '10px !important',
        width: '64px',
        maxWidth: '64px',
        flexBasis: '64px',
        overflow: 'hidden',
        alignItems: 'center',
    },
    expandButtonRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
        ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
        ...shorthands.borderTop('1px', 'solid', 'rgba(255, 255, 255, 0.2)'),
        boxSizing: 'border-box',
        flexShrink: 0,
    },
    expandButtonRowCollapsed: {
        justifyContent: 'center',
    },
    expandButton: {
        minWidth: 'auto',
        ...shorthands.padding('4px'),
        color: 'white',
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalXXS),
        ...shorthands.padding(tokens.spacingVerticalNone, tokens.spacingHorizontalNone),
        paddingBottom: tokens.spacingVerticalXS,
        alignItems: 'stretch',
    },
    sectionToggle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        color: customColors.white,
        fontWeight: 500,
        fontSize: tokens.fontSizeBase200,
        fontFamily: 'inherit',
        padding: '10px 20px',
        background: 'transparent',
        ...shorthands.border('none'),
        cursor: 'pointer',
        flexShrink: 0,
    },
    sectionHeaderRow: {
        display: 'flex',
        alignItems: 'stretch',
        width: '100%',
        backgroundColor: 'transparent',
        ':hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
        },
    },
    sectionHeaderActions: {
        display: 'flex',
        alignItems: 'stretch',
        flexShrink: 0,
    },
    sectionToggleMain: {
        flex: 1,
        minWidth: 0,
        justifyContent: 'flex-start',
        ...shorthands.gap(tokens.spacingHorizontalS),
        paddingRight: '0px',
    },
    sectionActionButton: {
        minWidth: '44px',
        width: '44px',
        color: customColors.white,
        backgroundColor: 'transparent',
        ...shorthands.border('none'),
        ...shorthands.padding('0px'),
        flexShrink: 0,
    },
    chatsHeaderLeft: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalXS),
        minWidth: 0,
    },
    chatCount: {
        marginLeft: '4px',
    },

    emptyState: {
        color: tokens.colorNeutralForeground3,
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    },
    listContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
    },
    scrollableSection: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        ...ScrollBarStyles,
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(235, 215, 215, 0.2)',
            borderRadius: '4px',
        },
        alignItems: 'stretch',
        overflowY: 'scroll',
    },
    recentlyUsedSection: {
        width: '100%',
        flexShrink: 0,
    },
    sectionDivider: {
        flexGrow: 0,
        flexShrink: 0,
    },

    collapsedContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
    },
    expandedContent: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
    },
    headerSection: {
        flexShrink: 0,
    },
    collapsedButtonsSection: {
        flexShrink: 0,
    },
    collapsedChatsSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingVerticalXXS),
        paddingBottom: tokens.spacingVerticalXS,
        width: '100%',
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    collapsedChatItem: {
        minWidth: '48px',
        width: '48px',
        minHeight: '48px',
        height: '48px',
        flexShrink: 0,
        ...shorthands.padding('0px'),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...shorthands.borderRadius('4px'),
        backgroundColor: 'transparent',
        ...shorthands.border('1px', 'solid', 'transparent'),
        cursor: 'pointer',
        boxSizing: 'border-box',
        ':hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
        },
    },
    collapsedSectionDivider: {
        width: '48px',
        maxWidth: '48px',
        alignSelf: 'center',
        flexShrink: 0,
        ...shorthands.margin('10px', tokens.spacingHorizontalNone),
    },
    collapsedChatItemSelected: {
        backgroundImage: 'linear-gradient(270deg, #01416B 66%, rgba(0, 64, 104, 0.1) 124.52%)',
        ...shorthands.borderColor('transparent'),
    },
    collapsedChatAvatar: {
        width: '32px',
        height: '32px',
        ...shorthands.borderRadius('4px'),
        objectFit: 'cover',
    },
    // --- Drawer overlay styles ---
    drawer: {
        display: 'flex',
        flexShrink: 0,
        minWidth: '15%',
        backgroundImage: `url(${ChatNavigationBackground})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        height: '100%',
        backgroundPosition: 'bottom center',
        flexDirection: 'column',
        ...shorthands.overflow('hidden'),
    },
    expandedDrawer: {
        minWidth: '40%',
        width: '40%',
        ...shorthands.transition('width', '0.3s', 'ease-in-out'),
        '@media (min-width: 577px) and (max-width: 769px)': {
            minWidth: '50%',
            width: '50%',
        },
        '@media (max-width: 576px)': {
            minWidth: '70%',
            width: '70%',
        },
    },
    drawerHeader: {
        backgroundColor: 'transparent',
    },
    drawerBody: {
        overflow: 'scroll',
        ...shorthands.padding('0px'),
        backgroundColor: 'transparent',
    },
});

const ChatList: React.FC = () => {
    const styles = useStyles();
    const dispatch = useAppDispatch();

    const { isCollapsed, isSmallScreen, isOverlay, handleExpand } = useChatListLayout();

    // view model should return all data needed by component. In this case: list of chat sessions, loading state, and callbacks for when a session is selected, created, deleted, or renamed
    const vm = useChatList();

    const [primaryOpen, setPrimaryOpen] = useState(true);
    const [archivedOpen, setArchivedOpen] = useState(true);
    const [isManageChatsOpen, setIsManageChatsOpen] = useState(false);

    const expandedOrCollapsed = isCollapsed || isSmallScreen;

    const handleOpenManageChats = () => {
        vm.clearSelection();
        setIsManageChatsOpen(true);
    };

    const handleManageChatsOpenChange = (open: boolean) => {
        setIsManageChatsOpen(open);

        if (!open) {
            vm.clearSelection();
        }
    };

    const renderChatListItem = (item: ChatListItemVm) => (
        <ChatListItem
            key={item.sessionId}
            sessionId={item.sessionId}
            title={item.title}
            isTitleGenerated={item.isTitleGenerated}
            providerType={item.providerType}
            timestampText={item.timestampText}
            avatarImageName={item.avatarImageName}
            capabilities={item.capabilities}
            isSelected={item.isSelected}
            forceExpanded
            onSelect={vm.selectSession}
            onDelete={vm.deleteSession}
            onRename={vm.renameSession}
            onDownload={vm.downloadSession}
        />
    );

    const renderCollapsedItem = (item: ChatListItemVm) => {
        const avatarSrc = buildAvatarSrc(item.providerType, item.avatarImageName);
        return (
            <Tooltip key={item.sessionId} content={item.title} relationship="label" positioning="after">
                <Button
                    className={mergeClasses(
                        styles.collapsedChatItem,
                        item.isSelected ? styles.collapsedChatItemSelected : undefined,
                    )}
                    onClick={() => vm.selectSession(item.sessionId)}
                >
                    <img src={avatarSrc} alt="" className={styles.collapsedChatAvatar} />
                </Button>
            </Tooltip>
        );
    };

    const renderListContent = (forceExpanded = false) => {
        const collapsed = expandedOrCollapsed && !forceExpanded;

        if (collapsed) {
            return (
                <div className={styles.collapsedContent}>
                    <div className={styles.collapsedButtonsSection}>
                        <ChatListHeader
                            onCreateChat={vm.createSession}
                            isCollapsed
                            isNewChatDisabled={vm.isMaxChatsReached}
                        />
                    </div>
                    <div className={styles.collapsedChatsSection}>
                        <Divider className={mergeClasses(styles.collapsedSectionDivider, styles.sectionDivider)} />
                        <div className={styles.list}>
                            {vm.primaryItems.map(renderCollapsedItem)}
                            {vm.archivedItems.length > 0 && (
                                <>
                                    <Divider
                                        className={mergeClasses(styles.collapsedSectionDivider, styles.sectionDivider)}
                                    />
                                    {vm.archivedItems.map(renderCollapsedItem)}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.expandedContent}>
                <div className={styles.headerSection}>
                    <ChatListHeader
                        onCreateChat={vm.createSession}
                        isCollapsed={false}
                        isNewChatDisabled={vm.isMaxChatsReached}
                    />
                </div>
                <div className={styles.scrollableSection}>
                    <Divider className={styles.sectionDivider} />
                    <div className={styles.recentlyUsedSection}>
                        <ChatListRecentlyUsed />
                    </div>
                    <Divider className={styles.sectionDivider} />
                    <div className={styles.sectionHeaderRow}>
                        <button
                            className={mergeClasses(styles.sectionToggle, styles.sectionToggleMain)}
                            onClick={() => setPrimaryOpen((open) => !open)}
                        >
                            <div className={styles.chatsHeaderLeft}>
                                <span>Active Chats </span>
                                <span className={styles.chatCount}>
                                    {vm.allowedChatCount !== null
                                        ? `${vm.activeChatCount}/${vm.allowedChatCount}`
                                        : `${vm.activeChatCount}`}
                                </span>
                            </div>
                        </button>
                        <div className={styles.sectionHeaderActions}>
                            <Button
                                appearance="transparent"
                                className={styles.sectionActionButton}
                                icon={<MoreHorizontal20Regular />}
                                aria-label="Manage chats"
                                onClick={handleOpenManageChats}
                            />
                            <button
                                className={styles.sectionActionButton}
                                aria-label={primaryOpen ? 'Collapse active chats' : 'Expand active chats'}
                                onClick={() => setPrimaryOpen((open) => !open)}
                            >
                                {primaryOpen ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                            </button>
                        </div>
                    </div>
                    {primaryOpen && (
                        <div className={styles.list}>{vm.primaryItems.map((item) => renderChatListItem(item))}</div>
                    )}
                    {vm.archivedItems.length > 0 && (
                        <>
                            <Divider className={styles.sectionDivider} />
                            <div className={styles.sectionHeaderRow}>
                                <button
                                    className={mergeClasses(styles.sectionToggle, styles.sectionToggleMain)}
                                    onClick={() => setArchivedOpen((o) => !o)}
                                >
                                    <span>Archived Chats</span>
                                </button>
                                <div className={styles.sectionHeaderActions}>
                                    <button
                                        className={styles.sectionActionButton}
                                        aria-label={archivedOpen ? 'Collapse archived chats' : 'Expand archived chats'}
                                        onClick={() => setArchivedOpen((o) => !o)}
                                    >
                                        {archivedOpen ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                                    </button>
                                </div>
                            </div>
                            {archivedOpen && (
                                <div className={styles.list}>
                                    {vm.archivedItems.map((item) => renderChatListItem(item))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    };

    // --- Small-screen Drawer overlay ---
    if (isSmallScreen && isOverlay) {
        return (
            <>
                {/* Collapsed strip behind the overlay */}
                <div className={mergeClasses(styles.root, styles.rootSmallScreen)}>
                    <div className={mergeClasses(styles.expandButtonRow, styles.expandButtonRowCollapsed)}>
                        <Button
                            appearance="transparent"
                            className={styles.expandButton}
                            icon={<ChevronDoubleRightFilled />}
                            onClick={handleExpand}
                        />
                    </div>
                </div>

                <Drawer
                    className={mergeClasses(styles.drawer, styles.expandedDrawer)}
                    open={isOverlay}
                    onOpenChange={(_e, data) => {
                        if (!data.open) {
                            dispatch(setIsOverlay(false));
                            dispatch(setIsCollapsed(true));
                        }
                    }}
                >
                    <DrawerHeader className={styles.drawerHeader}>
                        <DrawerHeaderTitle style={{ justifyContent: 'end' }}>
                            <Button
                                appearance="transparent"
                                className={styles.expandButton}
                                icon={<ChevronDoubleLeftFilled />}
                                onClick={handleExpand}
                            />
                        </DrawerHeaderTitle>
                    </DrawerHeader>
                    <DrawerBody className={styles.drawerBody}>{renderListContent(true)}</DrawerBody>
                </Drawer>
            </>
        );
    }

    // --- Normal / collapsed desktop rendering ---
    return (
        <div
            className={mergeClasses(
                styles.root,
                isCollapsed && !isSmallScreen ? styles.rootCollapsed : undefined,
                isSmallScreen ? styles.rootSmallScreen : undefined,
            )}
        >
            <div
                className={mergeClasses(
                    styles.expandButtonRow,
                    isCollapsed ? styles.expandButtonRowCollapsed : undefined,
                )}
            >
                <Button
                    appearance="transparent"
                    className={styles.expandButton}
                    icon={isCollapsed ? <ChevronDoubleRightFilled /> : <ChevronDoubleLeftFilled />}
                    onClick={handleExpand}
                />
            </div>
            <div className={styles.listContentWrapper}>{renderListContent()}</div>
            <ManageChatsDialog open={isManageChatsOpen} onOpenChange={handleManageChatsOpenChange} vm={vm} />
        </div>
    );
};

export default ChatList;
